import { z } from 'zod';

/**
 * Wire DTOs for the ERP register matching engine (SIL-264, W2-T4 of
 * resources/plans/erp-register-import-spec.md §2, §3.3).
 *
 * These schemas mirror the plain-TS shapes the pure engine
 * (`erp-register-matcher.ts`, `erp-register-triage.ts`,
 * `erp-register-precedence.ts`) operates on, for use at the actual API/DB
 * wire boundary Wave 3 builds. The pure engine intentionally does NOT import
 * from here (scenario-engine.ts precedent: plain TS in, Zod only at the
 * boundary) so its unit tests stay free of brand-cast friction. Asset ids
 * are `z.string()`, not the branded `uuidSchema` (common.ts) — same
 * reasoning: the pure engine's `OrgAssetRef.assetId` is a plain `string`, and
 * a branded type isn't assignable from an unbranded one without a cast at
 * every call site. Wave 3's real API routes still get full uuid validation
 * for free — every id flowing in ultimately came from a `uuidSchema`-parsed
 * DB read.
 *
 * `match_status` mirrors the DB review's exact persisted enum (spec §3.1):
 * `pending|matched|lump_partial|ambiguous|conflict|unmatched|dismissed`. A
 * heterogeneous-SKU lump stays `pending` (see erp-register-matcher.ts's
 * `ErpLinePendingHeterogeneousResult` doc) — `erpTriageKindSchema` below is
 * the richer, non-persisted discriminator the triage UI actually renders by.
 */

export const erpMatchStatusSchema = z.enum([
  'pending',
  'matched',
  'lump_partial',
  'ambiguous',
  'conflict',
  'unmatched',
  'dismissed',
]);
export type ErpMatchStatus = z.infer<typeof erpMatchStatusSchema>;

export const erpMatchedBySchema = z.enum(['serial', 'asset_tag', 'po']);
export type ErpMatchedBy = z.infer<typeof erpMatchedBySchema>;

/** One mapped ERP register line — the matcher's input contract (post
 * column-mapping, pre-persistence). See `ErpRegisterLineInput` in
 * `erp-register-matcher.ts` for field semantics. */
export const erpRegisterLineInputSchema = z.object({
  rowNum: z.number().int().positive(),
  serialRaw: z.string().nullable(),
  assetTagRaw: z.string().nullable(),
  poRef: z.string().nullable(),
  qty: z.number().int().positive().nullable(),
  costUsd: z.number().nullable(),
  erpReportedNbvUsd: z.number().nullable(),
});
export type ErpRegisterLineInput = z.infer<typeof erpRegisterLineInputSchema>;

/** Per-asset dollar allocation — one entry per asset a line's cost/NBV was
 * split across. */
export const erpAssetAllocationSchema = z.object({
  assetId: z.string(),
  costUsd: z.number().nullable(),
  erpReportedNbvUsd: z.number().nullable(),
});
export type ErpAssetAllocation = z.infer<typeof erpAssetAllocationSchema>;

/** Discriminated match-result shape — the wire mirror of
 * `ErpLineMatchResult` in `erp-register-matcher.ts`. */
export const erpLineMatchResultSchema = z.discriminatedUnion('status', [
  z.object({
    rowNum: z.number().int().positive(),
    status: z.literal('matched'),
    matchedBy: erpMatchedBySchema,
    allocations: z.array(erpAssetAllocationSchema).min(1),
  }),
  z.object({
    rowNum: z.number().int().positive(),
    status: z.literal('ambiguous'),
    matchedBy: erpMatchedBySchema,
    candidateAssetIds: z.array(z.string()).min(2),
  }),
  z.object({
    rowNum: z.number().int().positive(),
    status: z.literal('lump_partial'),
    matchedBy: z.literal('po'),
    declaredQty: z.number().int().positive(),
    unclaimedAssetIds: z.array(z.string()),
    proposedAllocations: z.array(erpAssetAllocationSchema),
    unallocatedCostUsd: z.number().nullable(),
    unallocatedNbvUsd: z.number().nullable(),
  }),
  z.object({
    rowNum: z.number().int().positive(),
    status: z.literal('pending'),
    triageReason: z.literal('heterogeneous_lump'),
    matchedBy: z.literal('po'),
    candidateAssetIds: z.array(z.string()).min(1),
  }),
  z.object({
    rowNum: z.number().int().positive(),
    status: z.literal('unmatched'),
  }),
]);
export type ErpLineMatchResult = z.infer<typeof erpLineMatchResultSchema>;

// ---------------------------------------------------------------------------
// Triage items (prototype learning #6 — exhaustive action set)
// ---------------------------------------------------------------------------

export const erpTriageActionSchema = z.enum([
  'keep_erp',
  'keep_existing',
  'accept_even_split',
  'accept_residual',
  'dismiss',
]);
export type ErpTriageAction = z.infer<typeof erpTriageActionSchema>;

export const erpTriageKindSchema = z.enum([
  'ambiguous_match',
  'heterogeneous_lump',
  'lump_partial',
  'conflict',
]);
export type ErpTriageKind = z.infer<typeof erpTriageKindSchema>;

export const erpTriageItemSchema = z.object({
  rowNum: z.number().int().positive(),
  kind: erpTriageKindSchema,
  availableActions: z.array(erpTriageActionSchema).min(1),
  candidateAssetIds: z.array(z.string()),
  proposedAllocations: z.array(erpAssetAllocationSchema).optional(),
  unallocatedCostUsd: z.number().nullable().optional(),
  unallocatedNbvUsd: z.number().nullable().optional(),
  conflictField: z.string().optional(),
  existingValue: z.unknown().optional(),
  incomingValue: z.unknown().optional(),
});
export type ErpTriageItem = z.infer<typeof erpTriageItemSchema>;

// ---------------------------------------------------------------------------
// Staging row (persisted representation, Wave 1 `erp_register_staging`)
// ---------------------------------------------------------------------------

/** The matcher's output folded onto its input line for persistence — one
 * `erp_register_staging` row (Wave 1, not yet implemented as of this ticket).
 * Deliberately excludes DB-only audit columns (`import_batch_id`,
 * `applied_at`, `dismissed_at`, ...) — those are Wave 1/3 concerns layered on
 * top of this matching-engine output. */
export const erpRegisterStagingRowSchema = z.object({
  rowNum: z.number().int().positive(),
  line: erpRegisterLineInputSchema,
  matchStatus: erpMatchStatusSchema,
  matchedAssetIds: z.array(z.string()),
  triage: erpTriageItemSchema.nullable(),
});
export type ErpRegisterStagingRow = z.infer<typeof erpRegisterStagingRowSchema>;

// ---------------------------------------------------------------------------
// Precedence (decision 4 / prototype learning #5)
// ---------------------------------------------------------------------------

export const erpImportSourceSchema = z.enum(['csv', 'erp']);
export type ErpImportSource = z.infer<typeof erpImportSourceSchema>;

/** `financial_source` (mig 679) widened with `manual` — the possible source
 * of an EXISTING stored value the precedence table is compared against. */
export const erpFieldSourceSchema = z.enum(['csv', 'erp', 'manual']);
export type ErpFieldSource = z.infer<typeof erpFieldSourceSchema>;

export const erpPrecedenceActionSchema = z.enum([
  'insert',
  'update',
  'no_op',
  'conflict',
  'skip_lower_precedence',
]);
export type ErpPrecedenceAction = z.infer<typeof erpPrecedenceActionSchema>;

// ---------------------------------------------------------------------------
// Match-run (SIL-267, W3-T7) - sync endpoint response shape.
// ---------------------------------------------------------------------------

/** Response for POST /api/client/erp/batches/:batchId/match-run. Match-run is
 * sync (spec 3.3 - hashmap work, ms-scale for 10k x 10k) and idempotent - a
 * re-run recomputes every open row's status fresh and overwrites it via
 * erp_register_apply_match_results, so calling it twice on an unchanged
 * staging set yields the same statusCounts. */
export const erpMatchRunResponseSchema = z.object({
  batchId: z.string().uuid(),
  rowsConsidered: z.number().int().nonnegative(),
  rowsUpdated: z.number().int().nonnegative(),
  statusCounts: z.record(erpMatchStatusSchema, z.number().int().nonnegative()),
});
export type ErpMatchRunResponse = z.infer<typeof erpMatchRunResponseSchema>;
